import React, { useEffect, useMemo, useState } from 'react';

import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import cx from 'classnames';
import { ASSIGNMENT_CREATE_MODES } from 'constants/assignments';
import {
  createTrackerFromAssignmentTemplate,
  useAssignmentTemplate
} from 'sdk';

import { ReactComponent as DocumentIcon } from 'pages/Teachers/Assignments/CreateMath/assets/document-icon.svg';
import ClassSelector from 'pages/Teachers/Assignments/CreateMath/components/ClassSelector';
import ExemplarUpload from 'pages/Teachers/Assignments/CreateMath/components/ExemplarUpload';
import RubricSelector from 'pages/Teachers/Assignments/CreateMath/components/RubricSelector';
import StandardSelector from 'pages/Teachers/Assignments/CreateMath/components/StandardSelector';
import TemplateDropdown from 'pages/Teachers/Assignments/CreateMath/components/TemplateDropdown';
import SwitchToggle from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/components/SwitchToggle';
import {
  assignmentAssign,
  useAllSectionsList
} from 'pages/Teachers/shared/AssignmentDialog/components/AssignAssignment/sdk';
import { VALUES_OPTIONS } from 'pages/Teachers/shared/AssignmentDialog/constants';
import { trackerCreate } from 'pages/Teachers/shared/AssignmentDialog/sdk';
import {
  buildAssignmentFormData,
  validateAssignmentForm
} from 'utils/assignments';
import { useEdLightTracking } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import { notifyError } from 'utils/notifications';

import styles from './styles.module.scss';

const CreateMathForm = ({ mode, setMode, onCreationSuccess }) => {
  const [assignmentName, setAssignmentName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedRubric, setSelectedRubric] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [shareAcrossDistricts, setShareAcrossDistricts] = useState(false);

  const [maxGradeValue, setMaxGradeValue] = useState(100);

  const { data: rawSectionsResponse, isLoading: isLoadingSections } =
    useAllSectionsList();

  const { trackEvent } = useEdLightTracking({
    feature: 'create',
    view: 'type'
  });

  const templateResult = useAssignmentTemplate({
    assignmentTemplateId: selectedTemplate?.assignment_template_id || null,
    params: { is_accelerated: selectedTemplate?.is_accelerated }
  });

  const isFormValid = validateAssignmentForm({
    mode,
    assignmentName,
    selectedSections,
    selectedRubric,
    selectedStandards,
    uploadedFile
  });

  const processSections = ({ data }) => {
    if (!Array.isArray(data)) return [];

    const validSections = data.filter(Boolean);

    const uniqueSections = Array.from(
      new Map(validSections.map((item) => [item.id, item])).values()
    );

    return uniqueSections;
  };

  const sections = useMemo(() => {
    const actualData = Array.isArray(rawSectionsResponse)
      ? rawSectionsResponse
      : rawSectionsResponse?.data || [];

    return processSections({ data: actualData });
  }, [rawSectionsResponse]);

  const handleCreateAssignment = async () => {
    setIsSubmitting(true);

    try {
      const formData = buildAssignmentFormData({
        assignmentName,
        description,
        uploadedFile,
        selectedRubric,
        selectedStandards,
        maxGradeValue,
        shareAcrossDistricts,
        isAccelerated: selectedTemplate?.is_accelerated || false,
        mode
      });

      trackEvent(`${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`, {
        view: 'create',
        form: 'create',
        pages: uploadedFile?.pages || 1,
        rubric: selectedRubric?.key || 'none',
        has_qa: false
      });

      let createSuccess, createData, createErrors;

      // Call the appropriate API based on the mode
      if (mode === ASSIGNMENT_CREATE_MODES.TEMPLATE) {
        const assignmentTemplateId = selectedTemplate?.assignment_template_id;

        if (!assignmentTemplateId) {
          notifyError('Please select a template before creating.');
          return;
        }

        const response = await createTrackerFromAssignmentTemplate({
          assignmentTemplateId,
          data: formData
        });

        createSuccess = response.success;
        createData = response.data;
        createErrors = response.errors;
      } else {
        const response = await trackerCreate(formData);

        createSuccess = response.success;
        createData = response.data;
        createErrors = response.errors;
      }

      if (!createSuccess) {
        notifyError(createErrors);
        return;
      }

      const defaultOpen = new Date();
      const defaultDue = new Date();
      defaultDue.setDate(defaultOpen.getDate() + 2);

      const assignmentPayload = {
        sections_data: selectedSections.map((section) => ({
          section_id: section.id,
          open: section.open
            ? new Date(section.open).toISOString()
            : defaultOpen.toISOString(),
          due: section.due
            ? new Date(section.due).toISOString()
            : defaultDue.toISOString()
        }))
      };

      trackEvent(`${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`, {
        view: 'assign',
        form: 'assign',
        classes: selectedSections?.length || 0
      });

      const { success: assignSuccess, errors: assignErrors } =
        await assignmentAssign(createData.id, assignmentPayload);

      if (!assignSuccess) {
        notifyError(assignErrors);
        return;
      }

      onCreationSuccess({ createdTrackerId: createData.id });
    } catch (error) {
      notifyError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMode = () => {
    const isSwitchingToScratch = mode === ASSIGNMENT_CREATE_MODES.TEMPLATE;

    setMode((prev) =>
      prev === ASSIGNMENT_CREATE_MODES.TEMPLATE
        ? ASSIGNMENT_CREATE_MODES.SCRATCH
        : ASSIGNMENT_CREATE_MODES.TEMPLATE
    );

    trackEvent(`${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`, {
      button: isSwitchingToScratch ? 'create' : 'template'
    });

    setSelectedTemplate(null);
    setUploadedFile(null);
  };

  const handleTemplateSelection = (template) => {
    setSelectedTemplate(template);
    if (template) {
      setAssignmentName(template.assignment_template_title);

      trackEvent(`${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`, {
        button: 'copy'
      });
    }
  };

  useEffect(() => {
    const data = templateResult?.data;

    if (!data) return;

    if (data.title) {
      setAssignmentName(data.title);
    }

    if (data.description) {
      setDescription(data.description);
    }

    let pagesCount = 1;

    if (data.tasks && data.tasks.length > 0) {
      const firstTask = data.tasks[0];
      pagesCount = data.tasks.length;

      if (firstTask.standards) {
        setSelectedStandards(firstTask.standards);
      }

      if (firstTask.value_type) {
        const presetOption = VALUES_OPTIONS.find(
          (opt) => opt.key === firstTask.value_type
        );

        if (presetOption) {
          setSelectedRubric(presetOption);
        }
      }

      if (firstTask.max_value !== undefined) {
        setMaxGradeValue(firstTask.max_value);
      }
    }

    if (data.attachments && data.attachments.length > 0) {
      const attachment = data.attachments[0];

      setUploadedFile({
        id: attachment.file.id,
        name: attachment.file.name,
        url: attachment.file.url,
        type: attachment.file.type,
        pages: pagesCount
      });
    } else {
      setUploadedFile({ pages: pagesCount });
    }
  }, [templateResult?.data]);

  return (
    <div className={styles.modalWrapper}>
      <div className={styles.container}>
        {/* --- HEADER --- */}
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <h3 className={styles.title}>Launch New Assignment</h3>
          </div>

          <div className={styles.assignmentTypeRow}>
            <div className={styles.typeLabel}>
              <DocumentIcon />
              <span>Assignment</span>
            </div>
            <button onClick={toggleMode} className={styles.modeToggle}>
              {mode === ASSIGNMENT_CREATE_MODES.TEMPLATE
                ? 'Create from scratch →'
                : 'Use a Template →'}
            </button>
          </div>
        </div>

        {/* --- FORM CONTENT --- */}
        <div className={styles.formContent}>
          {/* Template Search (Template Mode Only) */}
          {mode === ASSIGNMENT_CREATE_MODES.TEMPLATE && (
            <TemplateDropdown
              selectedTemplate={selectedTemplate}
              onSelectTemplate={handleTemplateSelection}
              trackEvent={trackEvent}
            />
          )}

          <input
            type="text"
            className={styles.inputField}
            placeholder="Assignment Name"
            value={assignmentName}
            onChange={(e) => setAssignmentName(e.target.value)}
          />

          <textarea
            className={cx(styles.inputField, styles.textArea)}
            placeholder="Add a Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />

          {/* Learning Standard (Scratch Mode Only) */}
          {mode === ASSIGNMENT_CREATE_MODES.SCRATCH && (
            <StandardSelector
              selectedStandards={selectedStandards}
              setSelectedStandards={setSelectedStandards}
            />
          )}

          <ClassSelector
            sections={sections}
            selectedSections={selectedSections}
            setSelectedSections={setSelectedSections}
            isLoading={isLoadingSections}
          />

          <RubricSelector
            options={VALUES_OPTIONS}
            selected={selectedRubric}
            onSelect={setSelectedRubric}
            maxGrade={maxGradeValue}
            onMaxGradeChange={(newValue) => setMaxGradeValue(newValue)}
          />

          {mode === ASSIGNMENT_CREATE_MODES.SCRATCH && (
            <>
              <ExemplarUpload file={uploadedFile} setFile={setUploadedFile} />
              {false && (
                <SwitchToggle
                  checked={shareAcrossDistricts}
                  onChange={(e) => setShareAcrossDistricts(e.target.checked)}
                />
              )}
            </>
          )}
        </div>

        {/* --- FOOTER --- */}
        <div className={styles.footer}>
          <button
            className={styles.createButton}
            disabled={!isFormValid || isSubmitting}
            onClick={handleCreateAssignment}
          >
            {isSubmitting ? (
              'Processing...'
            ) : (
              <>
                <span></span>
                CREATE ASSIGNMENT
                <ArrowForwardIcon />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateMathForm;
